export type Direction = "up" | "down" | "left" | "right";

const KEY_TO_DIRECTION: Readonly<Record<string, Direction | undefined>> = {
  ArrowUp: "up",
  ArrowDown: "down",
  ArrowLeft: "left",
  ArrowRight: "right",
};

function isVisible(element: HTMLElement): boolean {
  return !element.hidden && !element.closest(".hidden") && element.getClientRects().length > 0;
}

export function focusables(root: ParentNode = document): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(".focusable:not([disabled])")).filter(isVisible);
}

function center(rect: DOMRect): { x: number; y: number } {
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
}

export function findGeometricTarget(current: HTMLElement, elements: readonly HTMLElement[], direction: Direction): HTMLElement | undefined {
  const origin = center(current.getBoundingClientRect());
  const candidates = elements.filter((item) => item !== current).map((item) => {
    const point = center(item.getBoundingClientRect());
    const dx = point.x - origin.x;
    const dy = point.y - origin.y;
    const primary = direction === "left" ? -dx : direction === "right" ? dx : direction === "up" ? -dy : dy;
    const secondary = direction === "left" || direction === "right" ? Math.abs(dy) : Math.abs(dx);
    return { item, primary, secondary, point };
  });
  const forward = candidates.filter(({ primary }) => primary > 1);
  if (forward.length) {
    forward.sort((a, b) => (a.primary + a.secondary * 2) - (b.primary + b.secondary * 2));
    return forward[0]?.item;
  }

  // Predictable wrap: start from the opposite edge, then prefer alignment.
  candidates.sort((a, b) => {
    const edgeA = direction === "left" ? a.point.x : direction === "right" ? -a.point.x : direction === "up" ? a.point.y : -a.point.y;
    const edgeB = direction === "left" ? b.point.x : direction === "right" ? -b.point.x : direction === "up" ? b.point.y : -b.point.y;
    return edgeB - edgeA || a.secondary - b.secondary;
  });
  return candidates[0]?.item;
}

export interface DpadOptions {
  getRoot: () => ParentNode;
  onEscape: () => void;
  onAnswerScroll: (direction: "up" | "down") => void;
  onDigitChange: (index: number, delta: number) => void;
}

export function installDpad(options: DpadOptions): () => void {
  const listener = (event: KeyboardEvent): void => {
    if (event.key === "Escape") {
      event.preventDefault();
      options.onEscape();
      return;
    }
    const direction = KEY_TO_DIRECTION[event.key];
    if (!direction) return;

    const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (active?.matches("input, textarea") && active.dataset.dpadNavigation !== "true" && direction) return;
    if (active?.dataset.scrollPanel === "true" && (direction === "up" || direction === "down")) {
      event.preventDefault();
      options.onAnswerScroll(direction);
      return;
    }
    if (active?.dataset.digitIndex !== undefined && (direction === "up" || direction === "down")) {
      event.preventDefault();
      options.onDigitChange(Number(active.dataset.digitIndex), direction === "up" ? 1 : -1);
      return;
    }
    if (active?.dataset.digitIndex === "5" && direction === "right") {
      event.preventDefault();
      const pairButton = options.getRoot().querySelector<HTMLElement>("[data-action='redeem-pairing']:not([disabled])");
      pairButton?.focus();
      return;
    }

    event.preventDefault();
    const available = focusables(options.getRoot());
    if (!active || !available.includes(active)) {
      available[0]?.focus();
      return;
    }
    findGeometricTarget(active, available, direction)?.focus();
  };
  document.addEventListener("keydown", listener);
  return () => document.removeEventListener("keydown", listener);
}
